import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Box, Heading, SimpleGrid, Text, VStack } from "@chakra-ui/react";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

interface PhoneTypeBreakdown {
  mobile: number;
  landline: number;
  fixedVoip: number;
  nonFixedVoip: number;
  personal: number;
  tollFree: number;
  premium: number;
  voicemail: number;
  pager: number;
  unknown: number;
  noPhone: number;
}

interface SmsReachability {
  smsCapable: number;
  nonSmsCapable: number;
  noPhone: number;
}

interface ContactCompleteness {
  phoneAndEmail: number;
  phoneOnly: number;
  emailOnly: number;
  neither: number;
}

interface PhoneTypeBreakdownChartProps {
  phoneTypeBreakdown: PhoneTypeBreakdown;
  smsReachability: SmsReachability;
  contactCompleteness: ContactCompleteness;
}

export const PhoneTypeBreakdownChart = ({
  phoneTypeBreakdown,
  smsReachability,
  contactCompleteness,
}: PhoneTypeBreakdownChartProps) => {
  // Filter out zero values for cleaner chart
  const phoneTypes = [
    { label: "Mobile", value: phoneTypeBreakdown.mobile, color: "#10B981" },
    { label: "Landline", value: phoneTypeBreakdown.landline, color: "#EF4444" },
    {
      label: "Fixed VoIP",
      value: phoneTypeBreakdown.fixedVoip,
      color: "#F59E0B",
    },
    {
      label: "Non-Fixed VoIP",
      value: phoneTypeBreakdown.nonFixedVoip,
      color: "#8B5CF6",
    },
    {
      label: "Personal",
      value: phoneTypeBreakdown.personal,
      color: "#06B6D4",
    },
    {
      label: "Toll Free",
      value: phoneTypeBreakdown.tollFree,
      color: "#EC4899",
    },
    { label: "Unknown", value: phoneTypeBreakdown.unknown, color: "#6B7280" },
    { label: "No Phone", value: phoneTypeBreakdown.noPhone, color: "#D1D5DB" },
  ].filter((type) => type.value > 0);

  const phoneTypeData = {
    labels: phoneTypes.map((t) => t.label),
    datasets: [
      {
        data: phoneTypes.map((t) => t.value),
        backgroundColor: phoneTypes.map((t) => t.color),
        borderColor: "#fff",
        borderWidth: 3,
      },
    ],
  };

  // SMS Reachability
  const smsData = {
    labels: ["SMS Capable", "Non-SMS", "No Phone"],
    datasets: [
      {
        data: [
          smsReachability.smsCapable,
          smsReachability.nonSmsCapable,
          smsReachability.noPhone,
        ],
        backgroundColor: ["#10B981", "#EF4444", "#D1D5DB"],
        borderColor: "#fff",
        borderWidth: 3,
      },
    ],
  };

  // Contact Completeness
  const completenessData = {
    labels: ["Phone & Email", "Phone Only", "Email Only", "Incomplete"],
    datasets: [
      {
        data: [
          contactCompleteness.phoneAndEmail,
          contactCompleteness.phoneOnly,
          contactCompleteness.emailOnly,
          contactCompleteness.neither,
        ],
        backgroundColor: ["#10B981", "#339CFF", "#F59E0B", "#EF4444"],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          font: {
            size: 12,
            weight: "600",
          },
          color: "#1A202C",
          padding: 12,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(26, 32, 44, 0.95)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        borderColor: "#339CFF",
        borderWidth: 1,
      },
    },
  };

  const barOptions: any = {
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(26, 32, 44, 0.95)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        borderColor: "#339CFF",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Box
      bg="white"
      p={{ base: 4, md: 8 }}
      borderRadius={{ base: "16px", md: "24px" }}
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px solid"
      borderColor="gray.100"
    >
      <Heading
        size={{ base: "sm", md: "md" }}
        color="#1A202C"
        fontWeight="700"
        mb={6}
      >
        Contact Intelligence
      </Heading>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={{ base: 6, md: 8 }}>
        {/* Phone Type Distribution */}
        {phoneTypes.length > 0 && (
          <VStack align="stretch">
            <Text
              fontSize="sm"
              fontWeight="600"
              color="gray.600"
              textAlign="center"
              mb={2}
            >
              Phone Type Distribution
            </Text>
            <Box>
              <Doughnut data={phoneTypeData} options={doughnutOptions} />
            </Box>
          </VStack>
        )}

        {/* SMS Reachability */}
        <VStack align="stretch">
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.600"
            textAlign="center"
            mb={2}
          >
            SMS Reachability
          </Text>
          <Box>
            <Doughnut data={smsData} options={doughnutOptions} />
          </Box>
        </VStack>

        {/* Contact Completeness */}
        <VStack align="stretch">
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.600"
            textAlign="center"
            mb={2}
          >
            Contact Data Quality
          </Text>
          <Box>
            <Bar data={completenessData} options={barOptions} />
          </Box>
        </VStack>
      </SimpleGrid>
    </Box>
  );
};
